import {
  CLUSTERING_METHOD,
  dateToMonthYear,
  getFirstDateStringOfMonthYear,
  isUsedInCapitalizableProjectClustering,
  toMonthYearString,
} from '@common/neo.tax';
import isNil from 'lodash/isNil';

import {
  advancedFilter,
  AscClusterType,
  AscProjectAggregateFilter,
  AscProjectFilter,
  AscProjectSortFields,
  AscProjectsSummaryFilter,
  CapitalizationStatus,
  ConditionRow,
  Conditions,
  FilterField,
  FilterSortDataType,
  GetAscProjectsQuery,
  makeFilterOperator,
  CapitalizableProjectClusteringOverride,
  OperatorName,
  SortValueMapper,
} from '@neo.tax/shared';

import { type AscProject } from '../hooks';
import { AscProjectFilterableColumn, AscProjectSortableColumn } from '../types';

export const mapAscProjectSortField = (fieldName: string): AscProjectSortFields => {
  const fieldMap: Record<string, AscProjectSortFields> = {
    name: AscProjectSortFields.Name,
    productName: AscProjectSortFields.ProductName,
    projectOwnerName: AscProjectSortFields.ProjectOwnerName,
    capitalizationStartDate: AscProjectSortFields.CapitalizationStartDate,
    capitalizationEndDate: AscProjectSortFields.CapitalizationEndDate,
    capitalizationStatus: AscProjectSortFields.CapitalizationStatus,
    predictedCapitalizationStatus: AscProjectSortFields.PredictedCapitalizationStatus,
    startDate: AscProjectSortFields.StartDate,
    endDate: AscProjectSortFields.EndDate,
    ticketCount: AscProjectSortFields.TicketCount,
    workstreamNamesSearch: AscProjectSortFields.WorkstreamNamesSearch,
  };
  return fieldMap[fieldName] ?? AscProjectSortFields.Name;
};

// Both CAPITALIZED (actively capitalizing) and EXPENSED (fully amortized) mean the project
// has been capitalized at some point; only NOT_CAPITALIZED is treated as not capitalized.
const isCapitalized = (project: GetAscProjectsQuery['getAscProjects']['edges'][number]['node']): boolean =>
  project.capitalizationStatus === CapitalizationStatus.Capitalized ||
  project.capitalizationStatus === CapitalizationStatus.Expensed;

export const transformAscProject = (
  project: GetAscProjectsQuery['getAscProjects']['edges'][number]['node'],
): AscProject => ({
  ...project,
  startDate: project.startDate ? new Date(project.startDate) : null,
  endDate: project.endDate ? new Date(project.endDate) : null,
  capitalizationStartDate: project.capitalizationStartDate ? new Date(project.capitalizationStartDate) : null,
  capitalizationEndDate: project.capitalizationEndDate ? new Date(project.capitalizationEndDate) : null,
  capitalizationEndDateExtendedFrom: project.capitalizationEndDateExtendedFrom
    ? new Date(project.capitalizationEndDateExtendedFrom)
    : null,
  capitalizationStatus: project.capitalizationStatus,
  predictedCapitalizationStatus: project.predictedCapitalizationStatus ?? null,
  predictedCapitalizationStatusReason: project.predictedCapitalizationStatusReason ?? null,
  predictedCapitalizationStartDate: project.predictedCapitalizationStartDate
    ? new Date(project.predictedCapitalizationStartDate)
    : null,
  predictedCapitalizationEndDate: project.predictedCapitalizationEndDate
    ? new Date(project.predictedCapitalizationEndDate)
    : null,
  predictedCapitalizationEndDateReason: project.predictedCapitalizationEndDateReason ?? null,

  // add a new dynamic field for isCapitalized
  isCapitalized: isCapitalized(project),
});

export const mapAscProjectSortValues: SortValueMapper<AscProject, AscProjectSortableColumn> = (column, item) => {
  switch (column) {
    case 'name':
      return item.name || null;
    case 'workstreamNamesSearch':
      return item.workstreamNames?.join(', ') || null;
    case 'predictedCapitalizationStatus':
      return item.predictedCapitalizationStatus || null;
    case 'capitalizationStartDate':
      return item.capitalizationStartDate?.toISOString() || null;
    case 'capitalizationEndDate':
      return item.capitalizationEndDate?.toISOString() || null;
    case 'isCapitalized':
      return item.isCapitalized ? 'true' : 'false';
    case 'productName':
      return item.product?.name || null;
    case 'projectOwnerName':
      return item.projectOwner?.fullName || null;
  }
};

export const makeAscProjectFilterConditionMatcher =
  (conditions: Conditions<AscProjectFilterableColumn>, lastCompletedAscSyncPeriodEnd?: Date | string | null) =>
  (item: AscProject): boolean => {
    switch (conditions.booleanOperator) {
      case 'AND':
        return conditions.conditionRows.every(
          (conditionRow) => matchesAscProjectCondition(conditionRow, item, lastCompletedAscSyncPeriodEnd) !== false,
        );
      case 'OR':
        return conditions.conditionRows.some(
          (conditionRow) => matchesAscProjectCondition(conditionRow, item, lastCompletedAscSyncPeriodEnd) === true,
        );
    }
  };

/**
 * Returns `true` if the {@link item} matches the {@link conditionRow},
 * `false` if it doesn't, or `null` if it should be ignored. An ignored
 * condition row doesn't stop an `AND` from matching, but also isn't sufficient
 * for an `OR` to match.
 */
const matchesAscProjectCondition = (
  conditionRow: ConditionRow<AscProjectFilterableColumn>,
  item: AscProject,
  lastCompletedAscSyncPeriodEnd?: Date | string | null,
): boolean | null => {
  const actuals = mapAscProjectFilterValues(conditionRow.column, item, lastCompletedAscSyncPeriodEnd).map((av) =>
    av.toLowerCase(),
  );
  // If the value is `null`, ignore the condition. If the value is a `number`,
  // something's gone wrong: also ignore the condition.
  if (typeof conditionRow.value !== 'string') {
    return null;
  } else {
    const expected = conditionRow.value.toLowerCase();
    return makeFilterOperator(conditionRow.dataType, conditionRow.operator, expected)(actuals);
  }
};

const mapAscProjectFilterValues = (
  column: AscProjectFilterableColumn,
  item: AscProject,
  lastCompletedAscSyncPeriodEnd?: Date | string | null,
): string[] => {
  switch (column) {
    case 'name':
      return [item.name ?? ''];
    case 'workstreamNamesSearch':
      return item.workstreamNames ?? [];
    case 'capitalizationStartDate': {
      if (!item.capitalizationStartDate) {
        return [''];
      }
      return [toMonthYearString(dateToMonthYear(item.capitalizationStartDate))];
    }
    case 'capitalizationEndDate': {
      if (!item.capitalizationEndDate) {
        return [''];
      }
      return [toMonthYearString(dateToMonthYear(item.capitalizationEndDate))];
    }
    case 'predictedCapitalizationStatus':
      return [item.predictedCapitalizationStatus ?? ''];
    case 'isCapitalized':
      return item.isCapitalized ? ['true'] : ['false'];
    case 'productName':
      return [item.product?.name ?? ''];
    case 'projectOwnerName':
      return [item.projectOwner?.fullName ?? ''];
    case 'capitalizableProjectClustering':
      // The `matchingTarget` filter column is only surfaced in the UI when the company is on
      // capitalizable project clustering, so hardcoding CAPITALIZABLE_PROJECT here matches the
      // contract enforced by the column gating in `useIsCapitalizedProjectClustering`.
      return isUsedInCapitalizableProjectClustering(
        item,
        CLUSTERING_METHOD.CAPITALIZABLE_PROJECT,
        lastCompletedAscSyncPeriodEnd,
      )
        ? ['ACTIVE']
        : ['INACTIVE'];
  }
};

/**
 * Returns whether the row's (operator, value) resolves to "Active" semantics.
 * Used to translate the virtual `matchingTarget` filter into compound GraphQL
 * clauses on the override + capitalization-date fields.
 */
const matchingTargetRowResolvesToActive = (row: ConditionRow<AscProjectFilterableColumn>): boolean => {
  const isActiveValue = row.value === 'ACTIVE';
  return row.operator === OperatorName.IS_NOT ? !isActiveValue : isActiveValue;
};

/**
 * The badge "Active" / "Inactive" is computed from the override field, the
 * capitalization start/end dates, and the cluster type (see
 * `getCapitalizableProjectClusteringStatus` in @common/neo.tax). To filter on the badge
 * value, expand each `matchingTarget` row into a compound filter that mirrors the same
 * three "Active" clauses at the GraphQL layer:
 *   1. override = INCLUDED
 *   2. override IS NULL AND cap_start set AND (cap_end IS NULL OR cap_end >= boundary)
 *      AND activity is current (end_date IS NULL OR end_date >= boundary)
 *   3. override IS NULL AND cluster_type = MAIN AND cap_start IS NULL AND start_date IS NULL
 *      ("fresh MAIN" — a capitalizable project uploaded but not yet synced; commonly has
 *      zero tickets, which is why it was previously absent from the Active list).
 *
 * A cap_end in/after the last completed ASC sync's end month ("boundary" cap_end) still
 * counts as Active — it is an auto-set artifact of that sync's data horizon, not a
 * finalized end date. `boundaryMonthStart` is the first day of that month.
 *
 * Active and Inactive are exact complements, so the two clauses below partition the
 * override-IS-NULL space without overlap (verified against the badge in the spec).
 */
const buildCapitalizableProjectClusteringClause = (
  row: ConditionRow<AscProjectFilterableColumn>,
  boundaryMonthStart: string | null,
): AscProjectFilter => {
  const activeClause: AscProjectFilter = {
    or: [
      { capitalizableProjectClusteringOverride: { eq: CapitalizableProjectClusteringOverride.Included } },
      {
        and: [
          { capitalizableProjectClusteringOverride: { is: null } },
          { capitalizationStartDate: { isNot: null } },
          {
            or: [
              { capitalizationEndDate: { is: null } },
              ...(boundaryMonthStart ? [{ capitalizationEndDate: { gte: boundaryMonthStart } }] : []),
            ],
          },
          // Activity must be current: end_date (last activity) in/after the boundary month. Null
          // end_date stays active. Inert when there is no completed sync (boundaryMonthStart null).
          ...(boundaryMonthStart
            ? [{ or: [{ endDate: { is: null } }, { endDate: { gte: boundaryMonthStart } }] }]
            : []),
        ],
      },
      {
        // Fresh MAIN: no override, no cap_start yet, MAIN cluster, no effort start date.
        // The cap_start IS NULL guard keeps an already-capitalizing/ended MAIN project out
        // of this clause — those are covered (Active or Inactive) by the cap_start branches.
        and: [
          { capitalizableProjectClusteringOverride: { is: null } },
          { capitalizationStartDate: { is: null } },
          { clusterType: { eq: AscClusterType.CapitalizableProjectMain } },
          { startDate: { is: null } },
        ],
      },
    ],
  };
  const inactiveClause: AscProjectFilter = {
    or: [
      { capitalizableProjectClusteringOverride: { eq: CapitalizableProjectClusteringOverride.Excluded } },
      {
        and: [
          { capitalizableProjectClusteringOverride: { is: null } },
          {
            or: [
              {
                // cap_start IS NULL, excluding the fresh-MAIN case (Active above).
                and: [
                  { capitalizationStartDate: { is: null } },
                  {
                    or: [
                      { clusterType: { neq: AscClusterType.CapitalizableProjectMain } },
                      { startDate: { isNot: null } },
                    ],
                  },
                ],
              },
              {
                // Capitalization ended: cap_start set with a finalized (pre-boundary) cap_end.
                // The cap_start guard mirrors the badge, where an ended state is only reachable
                // once cap_start is set — without it a fresh-MAIN row with a stray cap_end could
                // match both filters.
                and: [
                  { capitalizationStartDate: { isNot: null } },
                  { capitalizationEndDate: { isNot: null } },
                  ...(boundaryMonthStart ? [{ capitalizationEndDate: { lt: boundaryMonthStart } }] : []),
                ],
              },
              // No recent activity: capitalizing (Active by its cap dates) but last activity
              // (end_date) predates the boundary month. Exact complement of the Active clause-2
              // activity branch, so Active/Inactive stay a partition. Only when a boundary exists.
              ...(boundaryMonthStart
                ? [
                    {
                      and: [
                        { capitalizationStartDate: { isNot: null } },
                        {
                          or: [
                            { capitalizationEndDate: { is: null } },
                            { capitalizationEndDate: { gte: boundaryMonthStart } },
                          ],
                        },
                        { endDate: { isNot: null } },
                        { endDate: { lt: boundaryMonthStart } },
                      ],
                    },
                  ]
                : []),
            ],
          },
        ],
      },
    ],
  };
  return matchingTargetRowResolvesToActive(row) ? activeClause : inactiveClause;
};

const partitionCapitalizableProjectClusteringRows = (
  conditionRows: readonly ConditionRow<AscProjectFilterableColumn>[],
): {
  capitalizableProjectClusteringRows: ConditionRow<AscProjectFilterableColumn>[];
  otherRows: ConditionRow<AscProjectFilterableColumn>[];
} => {
  const capitalizableProjectClusteringRows: ConditionRow<AscProjectFilterableColumn>[] = [];
  const otherRows: ConditionRow<AscProjectFilterableColumn>[] = [];
  for (const row of conditionRows) {
    if (row.column === 'capitalizableProjectClustering') {
      capitalizableProjectClusteringRows.push(row);
    } else {
      otherRows.push(row);
    }
  }
  return { capitalizableProjectClusteringRows, otherRows };
};

/**
 * Maps frontend filter conditions to backend GraphQL filter:
 * - `isCapitalized` boolean → `capitalizationStatus` enum (UI treats CAPITALIZED + EXPENSED as "capitalized").
 * - `matchingTarget` virtual column → compound (override + capitalization-date) clause matching the badge.
 */
export const mapAscProjectFilterConditions = (
  conditions: Conditions<AscProjectFilterableColumn> | undefined,
  lastCompletedAscSyncPeriodEnd?: Date | string | null,
): { and: AscProjectFilter[] } | { or: AscProjectFilter[] } => {
  if (!conditions) {
    return { and: [] };
  }

  const { capitalizableProjectClusteringRows, otherRows } = partitionCapitalizableProjectClusteringRows(
    conditions.conditionRows,
  );

  const transformedConditions: Conditions<FilterField<AscProjectFilter>> = {
    ...conditions,
    conditionRows: otherRows.map((row) => {
      if (row.column === 'isCapitalized') {
        // The `isCapitalized` UI toggle maps onto the backend `capitalizationStatus` enum,
        // which has three states: CAPITALIZED, EXPENSED (amortized — was capitalized), and
        // NOT_CAPITALIZED. Both CAPITALIZED and EXPENSED count as "capitalized" in the UI:
        //   isCapitalized=true  -> capitalizationStatus != NOT_CAPITALIZED
        //   isCapitalized=false -> capitalizationStatus == NOT_CAPITALIZED
        // dataType is ENUM because the comparison value is the enum string; the boolean
        // comparator only accepts 'true'/'false' and rejects enum values. A nil value is
        // passed through so an incomplete row is ignored by advancedFilter, as with any
        // other column.
        return {
          ...row,
          column: 'capitalizationStatus' as FilterField<AscProjectFilter>,
          dataType: FilterSortDataType.ENUM,
          operator: row.value === 'true' ? OperatorName.IS_NOT : OperatorName.IS,
          value: isNil(row.value) ? row.value : CapitalizationStatus.NotCapitalized,
        };
      }
      return row as ConditionRow<FilterField<AscProjectFilter>>;
    }),
  };

  const baseFilter = advancedFilter<AscProjectFilter>(transformedConditions);
  const boundaryMonthStart = lastCompletedAscSyncPeriodEnd
    ? getFirstDateStringOfMonthYear(dateToMonthYear(lastCompletedAscSyncPeriodEnd))
    : null;
  const capitalizableProjectClusteringClauses = capitalizableProjectClusteringRows.map((row) =>
    buildCapitalizableProjectClusteringClause(row, boundaryMonthStart),
  );

  if (conditions.booleanOperator === 'OR') {
    return { or: [...('or' in baseFilter ? baseFilter.or : []), ...capitalizableProjectClusteringClauses] };
  }
  return { and: [...('and' in baseFilter ? baseFilter.and : []), ...capitalizableProjectClusteringClauses] };
};

/**
 * Maps frontend filter conditions to backend GraphQL summary filter. Mirrors
 * `mapAscProjectFilterConditions`; the underlying GraphQL filter type is
 * `AscProjectAggregateFilter` (the element type of the summary filter's `and`/`or`).
 */
export const mapAscProjectsSummaryFilterConditions = (
  conditions: Conditions<AscProjectFilterableColumn> | undefined,
  lastCompletedAscSyncPeriodEnd?: Date | string | null,
): { and: AscProjectAggregateFilter[] } | { or: AscProjectAggregateFilter[] } => {
  if (!conditions) {
    return { and: [] };
  }

  const { capitalizableProjectClusteringRows, otherRows } = partitionCapitalizableProjectClusteringRows(
    conditions.conditionRows,
  );

  const transformedConditions: Conditions<FilterField<AscProjectsSummaryFilter>> = {
    ...conditions,
    conditionRows: otherRows.map((row) => {
      if (row.column === 'isCapitalized') {
        // See mapAscProjectFilterConditions for why capitalizationStatus uses an ENUM
        // dataType and why a nil value is passed through unchanged.
        return {
          ...row,
          column: 'capitalizationStatus' as FilterField<AscProjectsSummaryFilter>,
          dataType: FilterSortDataType.ENUM,
          operator: row.value === 'true' ? OperatorName.IS_NOT : OperatorName.IS,
          value: isNil(row.value) ? row.value : CapitalizationStatus.NotCapitalized,
        };
      }
      return row as ConditionRow<FilterField<AscProjectsSummaryFilter>>;
    }),
  };

  const baseFilter = advancedFilter<AscProjectsSummaryFilter>(transformedConditions);
  const boundaryMonthStart = lastCompletedAscSyncPeriodEnd
    ? getFirstDateStringOfMonthYear(dateToMonthYear(lastCompletedAscSyncPeriodEnd))
    : null;
  const capitalizableProjectClusteringClauses = capitalizableProjectClusteringRows.map((row) =>
    buildCapitalizableProjectClusteringClause(row, boundaryMonthStart),
  );

  if (conditions.booleanOperator === 'OR') {
    return {
      or: [
        ...('or' in baseFilter ? (baseFilter.or as AscProjectAggregateFilter[]) : []),
        ...capitalizableProjectClusteringClauses,
      ],
    };
  }
  return {
    and: [
      ...('and' in baseFilter ? (baseFilter.and as AscProjectAggregateFilter[]) : []),
      ...capitalizableProjectClusteringClauses,
    ],
  };
};
